import React, { useState, useMemo } from "react"
import { Modal, Typography, Button, Input, Form, message } from "antd"
import PropTypes from "prop-types"
import { PhoneInput } from "ui/Input"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useDispatch } from "react-redux"
import { updateUserInfo } from "store/update-email-phone-number"
import { useTranslation } from "react-i18next"
import styles from "./ChangeContactModal.module.css"

const { Title } = Typography

const normalizePhone = (raw) => {
  if (!raw || raw === "null") return null
  if (typeof raw === "object") return raw
  if (typeof raw === "string") {
    const str = raw.trim()
    // Only attempt JSON.parse if the string looks like JSON to avoid swallowing unexpected errors
    if (str.startsWith("{") || str.startsWith("[")) {
      try {
        const parsed = JSON.parse(str)
        if (parsed?.fullNumber) return parsed
      } catch (e) {
        // handle the parse failure explicitly so it's visible during development/debugging
        // keep falling back to treating the raw value as the full number
        // eslint-disable-next-line no-console
        console.warn("normalizePhone: failed to parse phone JSON:", e)
      }
    }
    return { fullNumber: raw }
  }
  return null
}

const ChangeContactModal = ({ open, onCancel, type, initialValue, setUpdatedEmail, setUpdatedPhone }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isEmail = type === "email"
  const encrypted_uuid = sessionStorage.getItem("oauth_temp_token")

  const normalizedInitialPhone = useMemo(() => normalizePhone(initialValue), [initialValue])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const phonePayloadValue = values.phone_number?.fullNumber ?? values.phone_number ?? null

      const payload = {
        encrypted_uuid,
        ...(isEmail ? { email: values.email } : { phone_number: phonePayloadValue })
      }

      setLoading(true)
      const res = await dispatch(updateUserInfo(payload))

      if (res?.success) {
        message.success(res.message)
        if (isEmail) {
          setUpdatedEmail(values.email)
        } else {
          // ensure we propagate the fullNumber string
          setUpdatedPhone(phonePayloadValue)
        }

        form.resetFields()
        onCancel()
      }
    } catch (err) {
      // validation or API error
      console.error("Validation or API error:", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered width={420} closeIcon={<span className={styles.closeIcon}>×</span>}>
      <Title level={4} className={styles.title}>
        {isEmail ? t("verification.change_email") : t("verification.change_phone")}
      </Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={isEmail ? { email: initialValue || "" } : { phone_number: normalizedInitialPhone || null }}
      >
        {isEmail ? (
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: t("verification.error.please_enter_your_email") },
              { type: "email", message: t("verification.error.please_enter_valid_email") }
            ]}
          >
            <Input size="large" placeholder="example@email.com" />
          </Form.Item>
        ) : (
          <Form.Item
            name="phone_number"
            label="Phone Number"
            rules={[
              { required: true, message: t("verification.error.phone_number_required") },
              { validator: (_, value) => validatePhoneNumber(value?.fullNumber) }
            ]}
          >
            <PhoneInput
              key={normalizedInitialPhone?.fullNumber}
              initialValue={normalizedInitialPhone?.fullNumber}
              initialCountryCode={normalizedInitialPhone?.initialCountryCode}
              onChange={(val) => form.setFieldsValue({ phone_number: val })}
            />
          </Form.Item>
        )}

        <div className={styles.buttonGroup}>
          <Button onClick={onCancel} size="large" className={styles.roundedButton}>
            {t("verification.cancel")}
          </Button>
          <Button type="primary" onClick={handleSave} size="large" loading={loading} className={styles.roundedButton}>
            {t("verification.save")}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

ChangeContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["email", "phone"]).isRequired,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setUpdatedEmail: PropTypes.func,
  setUpdatedPhone: PropTypes.func
}

export default ChangeContactModal
